/**
 * Mixed payment display SSOT (presentation only — no payment logic).
 * Credit card partial + bank transfer remainder.
 */
import {
  LDS_FINANCIAL_MONEY_TOLERANCE,
  resolveLdsFinancialState,
} from "./resolveLdsFinancialState.js";

export const MIXED_PAYMENT_LABELS = Object.freeze({
  PARTIAL_RECEIVED: "התקבל תשלום חלקי",
  BANK_TRANSFER_PENDING: "ממתין להעברה בנקאית",
  FULLY_PAID: "שולם במלואו",
});

export const MIXED_PAYMENT_DISPLAY_KEYS = Object.freeze({
  FULLY_PAID: "fully_paid",
  BANK_TRANSFER_PENDING: "bank_transfer_pending",
  MIXED_PARTIAL_BANK_TRANSFER: "mixed_partial_bank_transfer",
  NONE: "none",
});

function truthyBankTransferFlag(order) {
  return (
    order.bank_transfer === true ||
    order.bank_transfer === "true" ||
    order.bank_transfer === 1 ||
    order.later === true ||
    order.later === "true" ||
    order.later === 1
  );
}

function hasSettlementBankTransferRemaining(order) {
  const method =
    order?.settlement_decision?.method ??
    order?.settlementDecision?.method ??
    order?.post_payment_checkout_audit?.settlement_decision?.method;
  return String(method || "").trim() === "bank_transfer_remaining";
}

/** Bank transfer (or settlement-to-bank) still expected while order is not fully paid. */
export function hasPendingBankTransfer(order, fin = null) {
  if (!order || typeof order !== "object") return false;
  const financial = fin || resolveLdsFinancialState(order);
  if (financial.isFullyPaid) return false;
  if (truthyBankTransferFlag(order)) return true;
  if (hasSettlementBankTransferRemaining(order)) return true;
  return false;
}

/**
 * @param {object|null|undefined} order
 * @returns {{
 *   hasPartialCardPayment: boolean,
 *   hasPendingBankTransfer: boolean,
 *   remainingBalance: number|null,
 *   displayStatus: string|null,
 *   displayStatusKey: string,
 *   badges: Array<{ label: string, tone: string }>|null,
 *   applies: boolean,
 * }}
 */
export function resolveMixedPaymentDisplayState(order) {
  const empty = {
    hasPartialCardPayment: false,
    hasPendingBankTransfer: false,
    remainingBalance: null,
    displayStatus: null,
    displayStatusKey: MIXED_PAYMENT_DISPLAY_KEYS.NONE,
    badges: null,
    applies: false,
  };

  if (!order || typeof order !== "object") return empty;

  const fin = resolveLdsFinancialState(order);
  const tol = LDS_FINANCIAL_MONEY_TOLERANCE;
  const sumpaid = fin.paidAmount ?? 0;
  const remaining = fin.remainingBalance;

  const pendingBank = hasPendingBankTransfer(order, fin);
  const partialPaid =
    sumpaid > tol && (remaining == null || remaining > tol);

  const base = {
    hasPartialCardPayment: partialPaid,
    hasPendingBankTransfer: pendingBank,
    remainingBalance: remaining,
    displayStatus: null,
    displayStatusKey: MIXED_PAYMENT_DISPLAY_KEYS.NONE,
    badges: null,
    applies: false,
  };

  if (partialPaid && pendingBank) {
    return {
      ...base,
      applies: true,
      displayStatusKey: MIXED_PAYMENT_DISPLAY_KEYS.MIXED_PARTIAL_BANK_TRANSFER,
      displayStatus: `${MIXED_PAYMENT_LABELS.PARTIAL_RECEIVED} · ${MIXED_PAYMENT_LABELS.BANK_TRANSFER_PENDING}`,
      badges: [
        { label: MIXED_PAYMENT_LABELS.PARTIAL_RECEIVED, tone: "partial_paid" },
        { label: MIXED_PAYMENT_LABELS.BANK_TRANSFER_PENDING, tone: "bank_pending" },
      ],
    };
  }

  return base;
}
